import { useState } from "react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  Popover,
  TextField,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PermsType } from "../../Partial/PermissionChange.partial";
import {
  RemoteCompanyPerm,
  RemoteCompanyUnitsPerm,
  RemoteUsersPerm
} from "../../../../Domain/Types/FloorPlan/Permission.type";
import {
  capitalize,
  checkOptEqual,
  checkOptLabel,
  generateLabel
} from "./PermissionSelect.functions";
import { WeekdayPermissionDialog } from "./WeekdayPermission/WeekdayPermissionDialog";
import { PermissionSelectSingleChip } from "./PermissionSelectSingleChip/PermissionSelectSingleChip";
import { TimeRelatPermissionDialog } from "./TimeRelatePermission/TimeRelatePermissionDialog";

export type P = {
  type: string;
  isUsingTimeRelatedPerm: boolean;
  label: PermsType;
  disabledRemoteOpt: boolean;
  initialValue: PermissionUnitEntries[];
  entries: PermissionUnitEntries[];
  isEditable: boolean;
  handleChange: (e: any, value: any[]) => void;
  handleStricPermChange: (e: any, value: any) => void;
  handleActiveWkdayChange: (option: any, value: any[]) => void;
  handleTimeSpanChange: (option: any, value: number) => void;
};

export type PermissionUnitEntries = RemoteCompanyPerm | RemoteCompanyUnitsPerm | RemoteUsersPerm;

export function PermissionSelect({
  type,
  isUsingTimeRelatedPerm,
  label,
  disabledRemoteOpt,
  initialValue,
  entries,
  isEditable,
  handleChange,
  handleStricPermChange,
  handleActiveWkdayChange,
  handleTimeSpanChange
}: Readonly<P>) {
  const { t } = useTranslation();

  // handle strict perm popover
  const [anchorElStrict, setAnchorElStrict] = useState<HTMLElement | null>(null);
  const strictOpen = Boolean(anchorElStrict);
  const handleStrictPermPopoverOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorElStrict(event.currentTarget);
  const handleStrictPermPopoverClose = () => setAnchorElStrict(null);

  // handle weekday perm popover
  const [anchorElWkday, setAnchorElWkday] = useState<HTMLElement | null>(null);
  const wkdayOpen = Boolean(anchorElWkday);
  const handleWkdayPermPopoverOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorElWkday(event.currentTarget);
  const handleWkdayPermPopoverClose = () => setAnchorElWkday(null);

  const [isWkdayPermDialogOpen, setIsWkdayPermDialogOpen] = useState(false);
  const [isTimeRelatPermDialogOpen, setIsTimeRelatPermDialogOpen] = useState(false);

  // select each perm option
  const [selectOpt, setSelectOpt] = useState<PermissionUnitEntries | null>(null);

  // handle time relevant perm popover
  const [anchorElTimeRelat, setAnchorElTimeRelat] = useState<HTMLElement | null>(null);
  const timeRelatOpen = Boolean(anchorElTimeRelat);
  const handleTimeRelatPermPopoverOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorElTimeRelat(event.currentTarget);
  const handleTimeRelatPermPopoverClose = () => setAnchorElTimeRelat(null);

  return (
    <>
      <Autocomplete
        id={`perm-select-${type}-${label}`}
        data-testid={`perm-select-${type}-${label}`}
        multiple
        disableCloseOnSelect
        autoComplete
        disabled={disabledRemoteOpt || !isEditable}
        value={initialValue}
        options={entries ?? []}
        isOptionEqualToValue={(option, value) => checkOptEqual(label, option, value)}
        onChange={handleChange}
        renderInput={params => (
          <TextField
            {...params}
            variant="standard"
            label={t(generateLabel(label))}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: <>{disabledRemoteOpt ? <CircularProgress size={15} /> : null}</>
              }
            }}
          />
        )}
        getOptionLabel={option => checkOptLabel(label, option, t)}
        renderOption={(props, option) => (
          <li {...props} data-testid={`perm-select-opt${option.id}`} key={option.id}>
            {checkOptLabel(label, option, t)}
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...rest } = getTagProps({ index });
            return (
              <Chip
                key={key}
                {...rest}
                label={capitalize(checkOptLabel(label, option, t))}
                icon={
                  <PermissionSelectSingleChip
                    isUsingTimeRelatedPerm={isUsingTimeRelatedPerm}
                    option={option}
                    handleStrictPermPopoverOpen={handleStrictPermPopoverOpen}
                    handleStrictPermPopoverClose={handleStrictPermPopoverClose}
                    handleWkdayPermPopoverOpen={handleWkdayPermPopoverOpen}
                    handleWkdayPermPopoverClose={handleWkdayPermPopoverClose}
                    handleTimeRelatPermPopoverOpen={handleTimeRelatPermPopoverOpen}
                    handleTimeRelatPermPopoverClose={handleTimeRelatPermPopoverClose}
                    setIsWkdayPermDialogOpen={setIsWkdayPermDialogOpen}
                    setIsTimeRelatPermDialogOpen={setIsTimeRelatPermDialogOpen}
                    handleStricPermChange={handleStricPermChange}
                    setSelectOpt={setSelectOpt}
                  />
                }
              />
            );
          })
        }
      />

      {/* tooltip popover for strict permission */}
      <Popover
        id="mouse-over-popover"
        sx={{ pointerEvents: "none" }}
        open={strictOpen}
        anchorEl={anchorElStrict}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handleStrictPermPopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 0.7 }}>{t("StrictPermission_SelectOnChip")}</Typography>
      </Popover>

      {/* tooltip popover for weekday permissions */}
      <Popover
        id="mouse-over-popover"
        sx={{ pointerEvents: "none" }}
        open={wkdayOpen}
        anchorEl={anchorElWkday}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handleWkdayPermPopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 0.7 }}>{t("Restrict this permission to certain weekdays")}</Typography>
      </Popover>

      {/* tooltip popover for time-related permissions */}
      <Popover
        id="mouse-over-popover"
        sx={{ pointerEvents: "none" }}
        open={timeRelatOpen}
        anchorEl={anchorElTimeRelat}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={handleTimeRelatPermPopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 0.7 }}>{t("Set this permission to certain timeframe")}</Typography>
      </Popover>

      {/* dialog for set weekday permission */}
      <WeekdayPermissionDialog
        dialogOpen={isWkdayPermDialogOpen}
        option={selectOpt}
        initialPermWeekdays={selectOpt?.activeWeekdays}
        handleActiveWkdayChange={handleActiveWkdayChange}
        setIsWkdayPermDialogOpen={setIsWkdayPermDialogOpen}
      />

      {/* dialog for set time-related permission */}
      <TimeRelatPermissionDialog
        dialogOpen={isTimeRelatPermDialogOpen}
        option={selectOpt}
        initialPermTimeSpan={selectOpt?.hoursInAdvance}
        handleTimeSpanChange={handleTimeSpanChange}
        setIsTimeRelatPermDialogOpen={setIsTimeRelatPermDialogOpen}
      />
    </>
  );
}
